import { OutlineButton, PrimaryButton } from "@components/Buttons";
import { CustomInput, CustomTextarea } from "@components/Forms";
import DocumentTable from "@components/Tables/DocumentTable";
import { Breadcrumb } from "@components/UI";
import ConfirmDeleteModal from "@components/UI/ConfirmDeleteModal";
import FullScreenDotLoader from "@components/UI/FullScreenDotLoader";
import LoadingSpinner from "@components/UI/LoadingSpinner";
import queryKeys from "@configs/react-query/query-key";
import { showErrorToast, showSuccessToast } from "@configs/react-toastify/snackbar";
import { useFileGroupAPI } from "@hooks/useFileGroupAPI";
import { useMutationData } from "@hooks/useMutationData";
import { useTheme } from "@hooks/useTheme";
import { formatDateTime, normalizeResponseData, uppercase } from "@utils/common";
import UserNameWithId from "@components/UI/UserNameWithId";
import { formatUserNameWithId } from "@utils/userDisplay";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { CHARACTER_LENGTH } from "@constants/index";
import { updateDataInIndexedDb, STORES } from "@utils/indexedDb";

const TEST_ID = "archive-detail";
const tid = (suffix) => `${TEST_ID}-${suffix}`;
const fieldContainerTestId = (name) => tid(`field-${name}-container`);
const fieldLabelTestId = (name) => tid(`field-${name}-label`);
const fieldInputTestId = (name) => tid(`field-${name}-input`);
const fieldValueTestId = (name) => tid(`field-${name}-value`);

export default function ArchiveDetail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { archiveSeq } = useParams();
  const [isEditMode, setIsEditMode] = useState(false);
  const [archiveData, setArchiveData] = useState({});
  const [editingData, setEditingData] = useState({
    title: "",
    content: "",
  });
  const [isUnchanged, setIsUnchanged] = useState(true);
  const [hasErrors, setHasErrors] = useState(false);
  const [fileInfoModified, setFileInfoModified] = useState([]);
  const [documentCurrentPage, setDocumentCurrentPage] = useState(1);
  const [documentItemsPerPage] = useState(5);
  const { fileInfo, documentTotalData, getFileInformationPagination, uploadFilesAndGetOnSuccess } = useFileGroupAPI(
    archiveData?.fileGroupNo || 0,
    documentCurrentPage || 1,
  );

  // API mutations
  const getArchiveData = useMutationData(
    `${queryKeys.getArchiveById}/${archiveSeq}`,
    { method: "GET" },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          const normalizedData = normalizeResponseData(response);
          setArchiveData(normalizedData);
          setEditingData(normalizedData);
        }
      },
      onError: (error) => {
        if (error?.response?.status === 404) {
          navigate("/404", { state: { backTo: "/archive" } });
        } else {
          showErrorToast(t("bulletin.get-archive-error-title"));
        }
        console.error("Error getting archive data:", error);
      },
    },
  );

  const increaseViewCount = useMutationData(
    `${queryKeys.increaseViewCountArchive}/${archiveSeq}`,
    { method: "PUT" },
  );

  const updateArchive = useMutationData(
    `${queryKeys.updateArchive}/${archiveSeq}`,
    { method: "PUT" },
    {
      onSuccess: async (response) => {
        if (typeof response !== "string") {
          await updateDataInIndexedDb(STORES.ARCHIVES, response, response.archiveSeq);
          showSuccessToast(t("bulletin.archive-update-success"));

          // Handle file upload if files are selected
          if (newFiles?.length > 0) {
            const uploadFilesData = newFiles.reduce((acc, file, index) => {
              if (file instanceof File) {
                acc[`file_${index}`] = file;
              }
              return acc;
            }, {});

            const formData = new FormData();
            formData.append("targetSeq", archiveSeq);
            formData.append("targetTypeCd", "ARCHIVE");
            formData.append("fileGroupNo", archiveData.fileGroupNo);

            Object.entries(uploadFilesData).forEach(([key, file]) => {
              formData.append(key, file, file.name);
            });

            uploadFilesAndGetOnSuccess.mutate(formData);
          } else if (fileInfoSeqListToDelete?.length > 0) {
            getFileInformationPagination.mutate();
          }

          getArchiveData.mutate();
          setIsEditMode(false);
          setIsUnchanged(true);
        }
      },
      onError: (error) => {
        console.error("Error updating archive:", error);
        showErrorToast(t("bulletin.archive-update-error"));
      },
    },
  );

  // API call for deleting files
  const deleteFile = useMutationData(
    (fileInfoSeq) => `file_info/${fileInfoSeq}`,
    {
      method: "DELETE",
    },
    {
      onSuccess: () => {
        showSuccessToast(t("project.document.delete-success"));
        // Refresh file list after deletion
        if (archiveData.fileGroupNo && archiveData.fileGroupNo !== 0) {
          getFileInformationPagination.mutate();
          // Calculate new maximum number of pages
          const maxPages = Math.ceil((documentTotalData - 1) / documentItemsPerPage);
          // If current page exceeds maxPages, switch to the last valid page
          if (documentCurrentPage > maxPages && maxPages > 0) {
            setDocumentCurrentPage(maxPages);
          } else if (documentTotalData <= 1) {
            // If no files remain, reset to page 1
            setDocumentCurrentPage(1);
          }
        }
      },
      onError: (error) => {
        console.error("Error during delete file:", error);
        showErrorToast(t("project.document.delete-error"));
      },
    },
  );

  useEffect(() => {
    getArchiveData.mutateAsync();
  }, []);

  // Effects
  useEffect(() => {
    if (archiveSeq) {
      increaseViewCount.mutateAsync();
    }
  }, [archiveSeq]);

  useEffect(() => {
    if (archiveData.fileGroupNo && archiveData.fileGroupNo !== 0) {
      getFileInformationPagination.mutate();
    }
  }, [archiveData.fileGroupNo, documentCurrentPage]);

  useEffect(() => {
    // Calculate the maximum number of pages based on documentTotalData
    const maxPages = Math.ceil((documentTotalData || 0) / documentItemsPerPage);

    // If documentCurrentPage exceeds maxPages, switch to the last valid page
    if (documentCurrentPage > maxPages && maxPages > 0) {
      setDocumentCurrentPage(maxPages);
    } else if (documentTotalData === 0) {
      // If there are no files left, reset to page 1
      setDocumentCurrentPage(1);
    }
  }, [documentTotalData, documentItemsPerPage, documentCurrentPage]);

  useEffect(() => {
    setFileInfoModified(fileInfo);
  }, [fileInfo]);

  // File management logic
  const validFiles = fileInfoModified.filter((file) => file.fileInfoSeq || file.file);
  const hasBoth = validFiles.filter((file) => file.fileInfoSeq && file.file);
  const newFiles = validFiles.map((file) => file.file).filter(Boolean);
  const deletedFiles = fileInfo.filter((orig) => !validFiles.some((file) => file.fileInfoSeq === orig.fileInfoSeq));
  const fileInfoSeqListToDelete = [
    ...hasBoth.map((file) => file.fileInfoSeq),
    ...deletedFiles.map((file) => file.fileInfoSeq),
  ];

  useEffect(() => {
    setIsUnchanged(hasBoth.length === 0 && newFiles.length === 0 && deletedFiles.length === 0 && isUnchanged);
  }, [fileInfoModified]);

  // Validation
  useEffect(() => {
    const hasRequiredFields = editingData.title?.trim() && editingData.content?.trim();

    // Compare only title and content now
    const dataChanged = editingData.title !== archiveData.title || editingData.content !== archiveData.content;

    setHasErrors(!hasRequiredFields);
    setIsUnchanged(!dataChanged);
  }, [editingData, archiveData]);

  // Check if save button should be enabled
  const canSave = editingData.title?.trim() && editingData.content?.trim() && !isUnchanged;

  // Handlers
  const handleEdit = () => {
    setIsEditMode(true);
    setEditingData({ ...archiveData });
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setEditingData({ ...archiveData });
    setFileInfoModified(fileInfo);
  };

  const handleSave = () => {
    if (!hasErrors && !isUnchanged) {
      updateArchive.mutate({
        ...archiveData,
        ...editingData,
      });
    }
  };

  const handleInputChange = (field, value) => {
    setEditingData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // File management functions
  const handleUploadFile = () => {
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.multiple = true;
    fileInput.accept = "*/*";
    fileInput.onchange = (event) => {
      const files = Array.from(event.target.files);
      if (files.length > 0) {
        if (!archiveSeq) {
          showErrorToast("Missing archive sequence.");
          return;
        }

        const uploadFilesData = files.reduce((acc, file, index) => {
          if (file instanceof File) {
            acc[`file_${index}`] = file;
          }
          return acc;
        }, {});

        const formData = new FormData();
        formData.append("targetSeq", archiveSeq);
        formData.append("targetTypeCd", "ARCHIVE");
        formData.append("fileGroupNo", archiveData.fileGroupNo);

        Object.entries(uploadFilesData).forEach(([key, file]) => {
          formData.append(key, file, file.name);
        });

        uploadFilesAndGetOnSuccess.mutate(formData, {
          onSuccess: () => {
            // Refresh announcementData to update fileGroupNo
            getArchiveData.mutateAsync().then(() => {
              // Refresh file list with the current page
              getFileInformationPagination.mutate();
              // Calculate new number of pages based on updated documentTotalData
              const maxPages = Math.ceil((documentTotalData + files.length) / documentItemsPerPage);
              // Only update documentCurrentPage if the current page is invalid
              if (documentCurrentPage > maxPages && maxPages > 0) {
                setDocumentCurrentPage(maxPages);
              } else if (documentTotalData === 0 && files.length > 0) {
                setDocumentCurrentPage(1); // Reset to page 1 if there were no files before
              }
              // Otherwise, do not change documentCurrentPage
            });
          },
          onError: (error) => {
            console.error("Error during upload:", error);
            showErrorToast(t("common.files-upload-error"));
          },
        });
      } else {
        showErrorToast("No files selected");
      }
    };

    fileInput.click();
  };

  const handleDeleteFile = (fileToDelete) => {
    if (fileToDelete.fileInfoSeq) {
      // Delete existing file from server
      deleteFile.mutate(fileToDelete.fileInfoSeq);
    } else if (fileToDelete.file) {
      // Remove new file from local state
      setFileInfoModified((prev) => prev.filter((file) => file !== fileToDelete));
    }
  };

  // Document delete modal state
  const [showDeleteDocumentModal, setShowDeleteDocumentModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [deleteDocumentLoading, setDeleteDocumentLoading] = useState(false);

  const handleDeleteDocument = (documentToDelete) => {
    setDocumentToDelete(documentToDelete);
    setShowDeleteDocumentModal(true);
  };

  const handleConfirmDeleteDocument = () => {
    if (!documentToDelete) return;
    setDeleteDocumentLoading(true);

    deleteFile.mutate(documentToDelete.fileInfoSeq, {
      onSettled: () => {
        setDeleteDocumentLoading(false);
        setShowDeleteDocumentModal(false);
        setDocumentToDelete(null);
      },
    });
  };

  if (getArchiveData.isPending) {
    return (
      <LoadingSpinner />
    );
  }

  return (
    <div className={`${colors.bgSecondary}`} data-testid="archive-detail-page">
      {/* Header */}
      <div className="mb-4" data-testid="archive-detail-header">
        {/* Breadcrumb */}
        <Breadcrumb
          data-testid="archive-detail-breadcrumb"
          items={[
            { label: t("navbar.archive-list"), href: "/archive" },
            {
              label: t("navbar.archive-details"),
              href: null,
              onClick: isEditMode ? () => setIsEditMode(false) : undefined,
            },
            ...(isEditMode ? [{ label: t("bulletin.edit-archive") }] : []),
          ]}
          className="mb-4"
        />

        {/* Title */}
        <div className="flex items-center justify-between">
          <h1
            className={`text-2xl font-bold ${colors.textPrimary} truncate`}
            data-testid="archive-detail-page-title"
            title={archiveData?.title || ""}
          >
            {archiveData?.title || "LOADING..."}
          </h1>

        </div>
      </div>

      {/* Main Information Cards */}
      <div
        className={`rounded-2xl border ${colors.borderPrimary} ${colors.bgPrimary}`}
        data-testid="archive-detail-information-card"
      >
        <div className="flex flex-col lg:flex-row">
          {/* Archive Information Card */}
          <div className={`w-full p-6`}>
            <div className="flex items-center justify-between mb-2">
              <h2
                className={`text-lg font-bold ${colors.textPrimary}`}
                data-testid="archive-detail-information-title"
              >
                {uppercase(t("bulletin.archive-information-title"))}
              </h2>
              {isEditMode ? (
                <div className="flex items-center justify-end space-x-3" data-testid="archive-detail-edit-actions">
                  <OutlineButton data-testid="archive-detail-cancel-button" onClick={handleCancel}>
                    {t("common.cancel-button")}
                  </OutlineButton>
                  <PrimaryButton data-testid="archive-detail-save-button" onClick={handleSave} disabled={!canSave}>
                    {t("common.save-button")}
                  </PrimaryButton>
                </div>
              ) : (
                <PrimaryButton data-testid="archive-detail-edit-button" onClick={handleEdit}>
                  {t("bulletin.edit-archive")}
                </PrimaryButton>
              )}
            </div>

            {!isEditMode ? (
              <div className={`pt-4`} data-testid="archive-detail-content-view">
                <div data-testid={fieldContainerTestId("content")}>
                  <label className={`mb-2 block font-semibold ${colors.textSecondary}`} data-testid={fieldLabelTestId("content")}>
                    {t("bulletin.content")}
                  </label>
                  <CustomTextarea
                    data-testid={fieldValueTestId("content")}
                    rows={4}
                    value={archiveData?.content || ""}
                    title={archiveData?.content}
                    readOnly={true}
                  />
                </div>
              </div>
            ) : (
              <div className={`pt-4`} data-testid="archive-detail-form">
                <div data-testid={fieldContainerTestId("title")}>
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`} data-testid={fieldLabelTestId("title")}>
                    {t("bulletin.title")} <span className={colors.textRequired}>*</span>
                  </label>
                  <CustomInput
                    data-testid={fieldInputTestId("title")}
                    type="text"
                    placeholder={t("bulletin.enter-title")}
                    value={editingData.title}
                    onChange={(e) => handleInputChange("title", e.target.value)}
                    title={editingData.title}
                    maxLength={CHARACTER_LENGTH.COMMENT}
                  />
                </div>
                <div className="md:col-span-2 mt-6" data-testid={fieldContainerTestId("content")}>
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`} data-testid={fieldLabelTestId("content")}>
                    {t("bulletin.content")} <span className={colors.textRequired}>*</span>
                  </label>
                  <CustomTextarea
                    data-testid={fieldInputTestId("content")}
                    rows={4}
                    placeholder={t("bulletin.enter-content")}
                    value={editingData.content}
                    onChange={(e) => handleInputChange("content", e.target.value)}
                    title={editingData.content}
                    maxLength={CHARACTER_LENGTH.CONTENT}
                  />
                  <p className={`text-xs ${colors.textCharacterCount} mt-1`} data-testid="archive-detail-content-char-count">{(editingData.content || "").length}/{CHARACTER_LENGTH.CONTENT}</p>
                </div>
              </div>
            )}
            <div className={`space-y-0`} data-testid="archive-detail-metadata-section">
              {/* Single Row: All metadata fields in one line */}
              <div className={`grid grid-cols-5 gap-8 py-4 pt-6`}>
                <div className="min-w-0" data-testid={fieldContainerTestId("creator")}>
                  <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`} data-testid={fieldLabelTestId("creator")}>
                    {t("common.creator")}
                  </span>
                  {isEditMode ? (
                    <CustomInput
                      data-testid={fieldInputTestId("creator")}
                      type="text"
                      disabled={true}
                      value={formatUserNameWithId(archiveData?.createrName, archiveData?.createrId)}
                    />
                  ) : (
                    <UserNameWithId
                      name={archiveData?.createrName}
                      id={archiveData?.createrId}
                      layout="inline"
                      data-testid={fieldValueTestId("creator")}
                    />
                  )}
                </div>

                <div data-testid={fieldContainerTestId("creation-date")}>
                  <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`} data-testid={fieldLabelTestId("creation-date")}>
                    {t("bulletin.creation-date")}
                  </span>
                  {isEditMode ? (
                    <CustomInput
                      data-testid={fieldInputTestId("creation-date")}
                      type="text"
                      disabled={true}
                      value={formatDateTime(archiveData?.createdAt) || ""}
                    />
                  ) : (
                    <span
                      className={`text-sm font-medium ${colors.textMessage}`}
                      data-testid={fieldValueTestId("creation-date")}
                    >
                      {formatDateTime(archiveData?.createdAt) || "---"}
                    </span>
                  )}
                </div>

                <div className="min-w-0" data-testid="archive-detail-last-editor-field">
                  <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`}>
                    {t("common.last-modified-by")}
                  </span>
                  {isEditMode ? (
                    <CustomInput
                      data-testid="archive-detail-last-editor-input"
                      type="text"
                      disabled={true}
                      value={formatUserNameWithId(archiveData?.updaterName, archiveData?.updaterId)}
                    />
                  ) : (
                    <UserNameWithId
                      name={archiveData?.updaterName}
                      id={archiveData?.updaterId}
                      layout="inline"
                      data-testid="archive-detail-last-editor-value"
                    />
                  )}
                </div>

                <div data-testid="archive-detail-last-modified-field">
                  <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`}>
                    {t("common.last-modified-date")}
                  </span>
                  {isEditMode ? (
                    <CustomInput
                      data-testid="archive-detail-last-modified-input"
                      type="text"
                      disabled={true}
                      value={formatDateTime(archiveData?.updatedAt) || ""}
                    />
                  ) : (
                    <span
                      className={`text-sm font-medium ${colors.textMessage}`}
                      data-testid="archive-detail-last-modified-value"
                    >
                      {formatDateTime(archiveData?.updatedAt) || "---"}
                    </span>
                  )}
                </div>

                <div data-testid="archive-detail-view-count-field">
                  <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`}>
                    {t("bulletin.view-count")}
                  </span>
                  {isEditMode ? (
                    <CustomInput
                      data-testid="archive-detail-view-count-input"
                      type="text"
                      disabled={true}
                      value={archiveData?.viewCnt || "0"}
                    />
                  ) : (
                    <span
                      className={`text-sm font-medium ${colors.textMessage}`}
                      data-testid="archive-detail-view-count-value"
                    >
                      {archiveData?.viewCnt || "0"}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* File Information Section */}
      <div
        className={`rounded-3xl border ${colors.borderPrimary} ${colors.bgPrimary} px-6 pt-6 pb-2 mt-6`}
        data-testid="archive-detail-attachments-section"
      >
        <div className="flex items-center justify-between mb-2">
          <h2
            className={`font-medium ${colors.textPrimary}`}
            data-testid="archive-detail-attachments-title"
          >
            {uppercase(t("bulletin.attachment"))} ({documentTotalData || 0})
          </h2>
          <OutlineButton data-testid="archive-detail-upload-file-button" onClick={handleUploadFile}>
            {t("project.document.upload-file")}
          </OutlineButton>
        </div>

        {fileInfoModified && fileInfoModified.length > 0 ? (
          <div data-testid="archive-detail-documents-table">
          <DocumentTable
            data={fileInfoModified}
            onDelete={handleDeleteDocument}
            currentPage={documentCurrentPage}
            totalPages={Math.ceil((documentTotalData || 0) / documentItemsPerPage)}
            onPageChange={setDocumentCurrentPage}
            totalData={documentTotalData}
          />
          </div>
        ) : (
          <div
            className={`py-8 text-center ${colors.textSecondary}`}
            data-testid="archive-detail-no-documents"
          >
            <p>{t("project.no-documents-found")}</p>
          </div>
        )}
      </div>

      {/* Confirm Delete Document Modal */}
      <ConfirmDeleteModal
        data-testid="archive-detail-delete-document-modal"
        isOpen={showDeleteDocumentModal}
        onClose={() => setShowDeleteDocumentModal(false)}
        onConfirm={handleConfirmDeleteDocument}
        title={t("bulletin.delete-document")}
        description={t("bulletin.delete-document-description")}
        loading={deleteDocumentLoading}
      />
      <FullScreenDotLoader
        loading={uploadFilesAndGetOnSuccess.isPending}
        text={
          uploadFilesAndGetOnSuccess.isPending
            ? t("bulletin.uploading-archive") || "Uploading archive file..."
            : t("bulletin.upload-archive-file") || "Uploading archive file..."
        }
        size={80}
        speedMultiplier={1.2}
      />
    </div>
  );
}
